import React, { forwardRef, useState } from 'react';
import { InputProps, OutlinedTextFieldProps, SelectProps, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { filterValue, getValue } from './utils';

export type FilterSelectProps = Omit<OutlinedTextFieldProps, 'variant' | 'select'> & {
  multiple?: boolean;
  availableItems: Set<string>;
  value: string | string[];
};

interface IProps {
  value: string | string[];
}

const useInputStyles = makeStyles<IProps>()((theme, props) => ({
  root: {
    height: 32.75,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '120%',
    borderRadius: 32,
    backgroundColor: 'unset',
    '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) $notchedOutline': {
      borderColor: props.value?.length > 0 ? theme.palette.primary.main : theme.palette.custom.gray.main,
    },
  },
  notchedOutline:
    props.value?.length > 0
      ? {
          top: -4,
          borderWidth: 2,
          borderColor: theme.palette.primary.main,
        }
      : {},
}));

const useSelectStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(0.5, 2),
    backgroundColor: 'unset',
  },
  select: {
    minWidth: 'initial',
  },
  outlined: {
    '&$outlined': {
      paddingRight: theme.spacing(4),
      borderRadius: 32,
    },
  },
  icon: {
    marginRight: 0,
  },
  iconOutlined: {
    right: theme.spacing(1),
  },
}));

const useStyles = makeStyles()((theme) => ({
  unfocused: { '&&& fieldset': { borderColor: `${theme.palette.primary.main} !important` } },
}));

const defaultInputProps: InputProps = {};

const defaultSelectProps: SelectProps = {
  displayEmpty: true,
};

const FilterSelect = forwardRef((props: FilterSelectProps, ref: React.Ref<any>) => {
  const {
    value,
    children,
    disabled: disabledFromProps = false,
    SelectProps = defaultSelectProps,
    InputProps = defaultInputProps,
    availableItems,
    ...rest
  } = props;
  const availableValue = filterValue(value, availableItems);

  const { classes: inputClasses } = useInputStyles({ value: availableValue });
  const { classes: selectClasses } = useSelectStyles();
  const { classes, cx } = useStyles();
  const [unfocused, setUnfocused] = useState(false);

  const childrenCount = React.Children.count(children);
  const disabled = disabledFromProps || (SelectProps.displayEmpty ? childrenCount < 2 : childrenCount < 1);

  return (
    <TextField
      select
      ref={ref}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={getValue(value, SelectProps)}
      disabled={disabled}
      InputProps={{
        ...InputProps,
        classes: inputClasses,
      }}
      SelectProps={{
        ...SelectProps,
        MenuProps: {
          autoFocus: false,
        },
        className: cx({ [classes.unfocused]: unfocused }),
        classes: selectClasses,
        onOpen: () => setUnfocused(false),
        onClose: () => {
          setUnfocused(Boolean(SelectProps.multiple ? value?.length : value));
        },
      }}
      {...rest}
    >
      {children}
    </TextField>
  );
});

export default FilterSelect;
